import { useState, useEffect, useCallback } from 'react';
import { Mail, Send, Clock, Filter, Search, ChevronDown, ExternalLink, Loader2 } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { API } from './constants';

const SOURCE_LABELS = {
  crm: { label: 'CRM', color: '#1B2A52' },
  proposition: { label: 'Proposition', color: '#009893' },
  menu: { label: 'Menu', color: '#C9A84C' },
  carte: { label: 'Carte', color: '#8B2252' },
  bon_cadeau: { label: 'Bon Cadeau', color: '#059669' },
  menu_groupe: { label: 'Menu Groupe', color: '#7C3AED' },
};

export function CrmEmailLogs() {
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (sourceFilter) params.set('source', sourceFilter);
      const [emailsRes, statsRes] = await Promise.all([
        fetch(`${API}/api/crm/email-logs?${params}`),
        fetch(`${API}/api/crm/email-logs/stats`),
      ]);
      setEmails(await emailsRes.json());
      setStats(await statsRes.json());
    } catch {}
    setLoading(false);
  }, [sourceFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = emails.filter(e => {
    if (search) {
      const s = search.toLowerCase();
      return (e.to || '').toLowerCase().includes(s) ||
        (e.subject || '').toLowerCase().includes(s) ||
        (e.prospect_nom || '').toLowerCase().includes(s) ||
        (e.body || '').toLowerCase().includes(s);
    }
    return true;
  });

  const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) + ' ' +
      dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-[rgb(var(--lm-gold))]" /></div>;

  return (
    <div className="space-y-4" data-testid="crm-email-logs">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div className="lm-card p-3 text-center" data-testid="email-stats-total">
            <p className="text-2xl font-bold text-[rgb(var(--lm-text))]">{stats.total}</p>
            <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))]">Total emails</p>
          </div>
          {Object.entries(stats.by_source || {}).filter(([, v]) => v > 0).map(([src, count]) => (
            <div key={src} className="lm-card p-3 text-center">
              <p className="text-2xl font-bold" style={{ color: SOURCE_LABELS[src]?.color || '#666' }}>{count}</p>
              <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))]">{SOURCE_LABELS[src]?.label || src}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher email, sujet, destinataire..."
            className="pl-8 text-xs h-8 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" data-testid="email-search" />
        </div>
        <div className="flex gap-1">
          <button onClick={() => setSourceFilter('')}
            className={`px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all ${!sourceFilter ? 'bg-[rgb(var(--lm-gold))] text-white' : 'bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))] hover:bg-[rgb(var(--lm-border))]'}`}
            data-testid="filter-all">Tous</button>
          {Object.entries(SOURCE_LABELS).map(([key, { label, color }]) => (
            <button key={key} onClick={() => setSourceFilter(key)}
              className={`px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all ${sourceFilter === key ? 'text-white' : 'bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))] hover:bg-[rgb(var(--lm-border))]'}`}
              style={sourceFilter === key ? { backgroundColor: color } : {}}
              data-testid={`filter-${key}`}>{label}</button>
          ))}
        </div>
      </div>

      {/* Email list */}
      <div className="space-y-1.5">
        {filtered.length === 0 ? (
          <div className="text-center py-8 text-[rgb(var(--lm-text-muted))] text-sm">
            <Mail size={24} className="mx-auto mb-2 opacity-30" />
            <p>Aucun email envoye</p>
            <p className="text-[10px] mt-1">Les emails apparaitront ici apres envoi depuis CRM, Propositions, Menu ou Carte</p>
          </div>
        ) : (
          filtered.map(email => {
            const src = SOURCE_LABELS[email.source] || { label: email.source, color: '#666' };
            return (
              <div key={email.id} className="lm-card p-3 hover:border-[rgb(var(--lm-gold))]/30 transition-all cursor-default group" data-testid={`email-log-${email.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider text-white"
                        style={{ backgroundColor: src.color }}>{src.label}</span>
                      {email.prospect_nom && (
                        <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">{email.prospect_nom}</span>
                      )}
                    </div>
                    <p className="text-xs font-medium text-[rgb(var(--lm-text))] truncate">{email.subject}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-[10px] text-[rgb(var(--lm-text-muted))] flex items-center gap-1">
                        <Send size={9} /> {email.to}
                      </span>
                      <span className="text-[10px] text-[rgb(var(--lm-text-muted))] flex items-center gap-1">
                        <Mail size={9} /> {email.from}
                      </span>
                    </div>
                    {email.body && (
                      <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">{email.body}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] text-[rgb(var(--lm-text-muted))] flex items-center gap-1">
                      <Clock size={9} /> {formatDate(email.date)}
                    </p>
                    <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-green-500/10 text-green-500 font-medium mt-1 inline-block">
                      {email.status === 'sent' ? 'Envoye' : email.status}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {filtered.length > 0 && (
        <p className="text-center text-[10px] text-[rgb(var(--lm-text-muted))]">
          {filtered.length} email{filtered.length > 1 ? 's' : ''} affiche{filtered.length > 1 ? 's' : ''}
          {search && ` (filtre: "${search}")`}
        </p>
      )}
    </div>
  );
}
